import React from 'react';
import './VotePage.scss';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
    asyncCreateVote,
    asyncFetchElection,
} from 'store/reducers/elections/thunks';
import { clearElection } from 'store/reducers/elections';
import { ICandidate, IElection } from 'store/reducers/elections/types';
import { IEmployee, IStudent } from 'store/reducers/user/types';
import { currentSeason, SeasonState } from 'js/season';
import { showToast } from 'components/redesign/Toast';
import { ReactComponent as CheckIcon } from 'assets/icons/redesign/check-circle-fill.svg';
import ProtivVsehImage from 'assets/images/protiv_vseh.png';
import { hasElectionAccess } from '../ElectionOutlet';

type Choice =
    | { kind: 'candidate'; candidate: ICandidate }
    | { kind: 'against-all' };

type Step = 'select' | 'confirm' | 'done';
function hasStoredToken(): boolean {
    try {
        return Boolean(
            typeof window !== 'undefined' &&
                window.localStorage &&
                window.localStorage.getItem('userToken')
        );
    } catch (e) {
        return false;
    }
}
function blockReason(
    election: IElection,
    user: IStudent | IEmployee | undefined,
    season: SeasonState
): string | undefined {
    if (season === 'soon') {
        return 'Голосование ещё не началось';
    }
    if (season === 'closed' || season === 'results') {
        return 'Голосование уже закрыто';
    }
    if (!user) return 'Войди, чтобы проголосовать';
    if (!hasElectionAccess(user, election)) {
        return 'Эти выборы не для тебя';
    }
    if (election.voted) return 'Твой голос уже учтён';
    if (!election.is_active) return 'Голосование сейчас недоступно';
    if (!election.candidates.length) return 'Кандидаты ещё не зарегистрированы';
    return undefined;
}

function choiceName(choice: Choice): string {
    if (choice.kind === 'against-all') return 'Против всех';
    return `${choice.candidate.first_name} ${choice.candidate.last_name}`;
}

function choiceKey(choice: Choice): string {
    return choice.kind === 'candidate'
        ? `c${choice.candidate.id}`
        : 'against-all';
}

function OptionContent({ choice }: { choice: Choice }): React.ReactElement {
    return (
        <>
            {choice.kind === 'candidate' ? (
                <span
                    className="rd-ballot__avatar"
                    style={{
                        backgroundImage: `url(${choice.candidate.picture})`,
                    }}
                />
            ) : (
                <span
                    className="rd-ballot__avatar rd-ballot__avatar--against"
                    style={{ backgroundImage: `url(${ProtivVsehImage})` }}
                    aria-hidden="true"
                />
            )}
            <span className="rd-ballot__name">{choiceName(choice)}</span>
            {choice.kind === 'candidate' ? (
                <span className="rd-ballot__sub">
                    курс {choice.candidate.course},{' '}
                    {choice.candidate.group.name}
                </span>
            ) : (
                <span className="rd-ballot__sub">
                    Голос против всех кандидатов
                </span>
            )}
        </>
    );
}

function OptionCard({
    choice,
    selected,
    onSelect,
}: {
    choice: Choice;
    selected: boolean;
    onSelect: () => void;
}): React.ReactElement {
    const className = [
        'rd-ballot__option',
        selected ? 'rd-ballot__option--selected' : '',
    ]
        .filter(Boolean)
        .join(' ');
    return (
        <button
            type="button"
            className={className}
            onClick={onSelect}
            aria-pressed={selected}
        >
            <OptionContent choice={choice} />
            {selected && (
                <span className="rd-ballot__check" aria-hidden="true">
                    <CheckIcon />
                </span>
            )}
        </button>
    );
}
function VotePage(): React.ReactElement {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { electionId } = useParams();
    const { search } = useLocation();
    const { user, sessionError } = useAppSelector(state => state.userStore);
    const { election } = useAppSelector(state => state.electionsStore);
    const season = currentSeason(election);

    const [choice, setChoice] = React.useState<Choice | undefined>(undefined);
    const [step, setStep] = React.useState<Step>('select');
    const [submitting, setSubmitting] = React.useState(false);
    const [loadState, setLoadState] = React.useState<
        'loading' | 'ready' | 'error'
    >('loading');
    const [request, retry] = React.useReducer(value => value + 1, 0);
    const [submitError, setSubmitError] = React.useState(false);
    const submittingRef = React.useRef(false);

    React.useEffect(() => {
        let active = true;
        setLoadState('loading');
        dispatch(asyncFetchElection(Number(electionId)))
            .unwrap()
            .then(
                () => {
                    if (active) setLoadState('ready');
                },
                () => {
                    if (active) setLoadState('error');
                }
            );
        return () => {
            active = false;
            dispatch(clearElection());
        };
    }, [dispatch, electionId, request]);

    const backToElection = React.useCallback(
        (message: string): void => {
            showToast(message, 'warning');
            navigate(`/elections/${electionId}${search}`, { replace: true });
        },
        [navigate, electionId, search]
    );
    const stillResolvingUser = hasStoredToken() && !user && !sessionError;
    const reason =
        loadState === 'ready' &&
        election &&
        !stillResolvingUser &&
        step === 'select'
            ? blockReason(election, user, season)
            : undefined;

    React.useEffect(() => {
        if (reason) backToElection(reason);
    }, [reason, backToElection]);

    const onConfirm = React.useCallback(() => {
        if (!choice || submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setSubmitError(false);
        dispatch(
            asyncCreateVote({
                election: Number(electionId),
                candidate:
                    choice.kind === 'candidate'
                        ? choice.candidate.id
                        : undefined,
            })
        )
            .unwrap()
            .then(() => {
                setStep('done');
                dispatch(asyncFetchElection(Number(electionId)));
            })
            .catch(() => {
                submittingRef.current = false;
                setSubmitting(false);
                setSubmitError(true);
                showToast(
                    'Не получилось отправить голос — попробуй ещё раз',
                    'warning'
                );
            });
    }, [choice, dispatch, electionId]);

    if (loadState === 'error') {
        return (
            <div className="rd-container rd-sections">
                <section className="rd-block rd-ballot__state" role="alert">
                    <h1 className="rd-h1">Не удалось загрузить бюллетень</h1>
                    <p>Проверь соединение и попробуй ещё раз.</p>
                    <button
                        type="button"
                        className="rd-btn rd-btn--green"
                        onClick={retry}
                    >
                        Повторить
                    </button>
                </section>
            </div>
        );
    }

    if (loadState === 'loading' || !election || stillResolvingUser || reason) {
        return (
            <div className="rd-container rd-sections">
                <p className="rd-ballot__loading">Загрузка</p>
            </div>
        );
    }

    if (step === 'done') {
        return (
            <div className="rd-container rd-sections">
                <section className="rd-block rd-ballot__done">
                    <h1 className="rd-h1">Твой голос учтён</h1>
                    <p className="rd-ballot__doneHint">
                        Спасибо за участие в выборах. Голос анонимный, изменить
                        его теперь нельзя.
                    </p>
                    <Link
                        className="rd-btn rd-btn--dark"
                        to={`/elections/${electionId}${search}`}
                    >
                        Вернуться к выборам
                    </Link>
                </section>
            </div>
        );
    }

    if (step === 'confirm' && choice) {
        return (
            <div className="rd-container rd-sections">
                <section className="rd-block">
                    <button
                        type="button"
                        className="rd-ballot__back"
                        onClick={() => setStep('select')}
                    >
                        ← Изменить выбор
                    </button>
                    <h1 className="rd-h1">Подтверди голос</h1>
                    <div
                        className={[
                            'rd-ballot__option',
                            'rd-ballot__option--selected',
                            'rd-ballot__option--static',
                        ].join(' ')}
                    >
                        <OptionContent choice={choice} />
                    </div>
                    <p className="rd-ballot__doneHint">
                        Это последний шаг — дальше голос не изменить.
                    </p>
                    {submitError ? (
                        <p className="rd-ballot__submitError" role="alert">
                            Не получилось отправить голос. Проверь соединение и
                            нажми «Подтвердить голос» ещё раз.
                        </p>
                    ) : null}
                    <div className="rd-ballot__actions">
                        <button
                            type="button"
                            className="rd-btn rd-btn--plain"
                            onClick={() => setStep('select')}
                            disabled={submitting}
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            className="rd-btn rd-btn--green"
                            onClick={onConfirm}
                            disabled={submitting}
                        >
                            {submitting ? 'Отправляем…' : 'Подтвердить голос'}
                        </button>
                    </div>
                </section>
            </div>
        );
    }

    const options: Choice[] = [
        ...election.candidates.map(
            (candidate): Choice => ({ kind: 'candidate', candidate })
        ),
        { kind: 'against-all' },
    ];

    return (
        <div className="rd-container rd-sections">
            <section className="rd-block">
                <Link
                    className="rd-ballot__back"
                    to={`/elections/${electionId}${search}`}
                >
                    ← Вернуться к выборам
                </Link>
                <h1 className="rd-h1">Бюллетень</h1>
                <p className="rd-ballot__hint">
                    Выбери одного кандидата или «Против всех» — голос анонимный,
                    изменить его после отправки нельзя.
                </p>
                <div className="rd-ballot__options">
                    {options.map(option => (
                        <OptionCard
                            key={choiceKey(option)}
                            choice={option}
                            selected={
                                choice !== undefined &&
                                choiceKey(choice) === choiceKey(option)
                            }
                            onSelect={() => setChoice(option)}
                        />
                    ))}
                </div>
                <div className="rd-ballot__actions">
                    <button
                        type="button"
                        className="rd-btn rd-btn--green"
                        onClick={() => setStep('confirm')}
                        disabled={!choice}
                    >
                        Продолжить
                    </button>
                </div>
            </section>
        </div>
    );
}

export default React.memo(VotePage);
